import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileBarChart2, Download, Printer, RefreshCw, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelReports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('travel_request_register');
  const [reportData, setReportData] = useState([]);

  useEffect(() => {
    fetchReport(reportType);
  }, [reportType]);

  const fetchReport = async (type) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/reports?report_type=${type}`, {
        withCredentials: true,
      });
      setReportData(data.data || []);
    } catch (err) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const reportTypes = [
    { id: 'travel_request_register', label: '1. Travel Request Register' },
    { id: 'traveller_wise_register', label: '2. Traveller-wise Register' },
    { id: 'ticket_booking_register', label: '3. Ticket Booking Register' },
    { id: 'advance_wise_register', label: '4. Advance-wise Register' },
    { id: 'daily_expense_report', label: '5. Daily Expense Report' },
    { id: 'advance_utilisation_75_report', label: '6. 75% Utilisation Report' },
    { id: 'additional_advance_approval_report', label: '7. Additional Advance Approvals' },
    { id: 'travel_extension_report', label: '8. Travel Extensions' },
    { id: 'pending_bill_submission_report', label: '9. Pending Bills Report' },
    { id: 'advance_settlement_report', label: '10. Advance Settlement Report' },
    { id: 'company_reimbursement_payable_report', label: '11. Reimbursement Payable' },
    { id: 'employee_refund_recovery_report', label: '12. Refund / Recovery' },
    { id: 'travel_closure_report', label: '13. Travel Closure' },
    { id: 'requester_vs_traveller_report', label: '14. Requester vs Traveller' },
  ];

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <FileBarChart2 className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Travel Desk Registers &amp; Audit Reports</h1>
            <p className="text-xs text-blue-200">
              Corporate Travel Spend, Department Registers, Mode Analysis &amp; Audit Log Statements.
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          {hasPermission(user, 'travel_desk.reports.view') && (
            <button className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Sub-report Tabs */}
      <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-sm flex flex-wrap gap-2">
        {reportTypes.map((rt) => (
          <button
            key={rt.id}
            onClick={() => setReportType(rt.id)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              reportType === rt.id ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {rt.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800 flex justify-between">
          <span>Report Output ({reportData.length} records)</span>
          <span className="capitalize text-blue-600 font-mono">{reportType} Report</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Generating report...</div>
        ) : reportData.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-semibold italic">No data found for this report type.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] border-b border-slate-200">
                <tr>
                  {Object.keys(reportData[0] || {}).map((col) => (
                    <th key={col} className="p-3.5 capitalize">
                      {col.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {reportData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    {Object.values(row).map((val, cIdx) => (
                      <td key={cIdx} className="p-3.5">
                        {String(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelReports;
